import React from 'react';
import { useFormikContext } from 'formik';
import { ILandingPageBuilderForm } from '../../types';
import AdditionalTextEditor from '../AdditionalTextEditor';
import { FormSection } from './shared/FormSection';
import { FormContainer } from './shared/FormContainer';
import { FormikColorPicker } from 'lib/components/formik/FormikColorPicker';
import { NBSP_REG } from 'lib/utils/regexes';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { FontFamilyOption } from 'app/pages/design/landingPageBuilder/components';
import { LP_BUILDER_FORM_MODE, LpBuilderFormMode } from '../../constants';
import { ILandingPageBuilderBulkEditFormData } from '../../helpers/getInitialLandingPageValuesForBulkEdit';
import { useCovideoTheme } from 'react-covideo-common';

interface IBodyFormProps {
  allFontFamilyOptions: FontFamilyOption[];
  mode: LpBuilderFormMode;
}

export const BodyForm = ({ allFontFamilyOptions, mode }: IBodyFormProps) => {
  const themes = useCovideoTheme();
  const { values, setFieldValue } = useFormikContext<
    ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  >();
  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;

  const commonFormikColorPickerStyle = {
    labelStyles: {
      fontSize: '12px',
      color: themes.colors.neutral[80],
      fontWeight: 600,
      marginBottom: '8px',
    },
  };

  const handleColorPickerInputChange = (
    field: keyof ILandingPageBuilderBulkEditFormData,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const hex = e.target.value;
    if (isBulkEditMode) {
      // in bulk edit mode, allow empty value
      if (hex === '') {
        setFieldValue(field, null);
        return;
      }
    }
  };

  const handleTextEditorChange = (text: string) => {
    if (isBulkEditMode && text.trim() === '') {
      setFieldValue('additionalText', null);
      return;
    }
    const cleanedTextValue = text.replace(NBSP_REG, '');
    setFieldValue('additionalText', cleanedTextValue);
  };

  return (
    <FormContainer title='Body'>
      <FormSection title='Color'>
        <FormikColorPicker
          label='Background Color'
          name='bodyBackgroundColor'
          extendStyles={commonFormikColorPickerStyle}
          onInputChange={e =>
            handleColorPickerInputChange('bodyBackgroundColor', e)
          }
        />
      </FormSection>
      <FormSection title='Slogan'>
        <AdditionalTextEditor
          initialContent={values.additionalText ?? undefined}
          onTextEditorChange={text => handleTextEditorChange(text)}
        />
        <FormikDropdownField
          options={allFontFamilyOptions}
          name={'additionalTextFontFamily'}
          creatable={false}
          placeholder='Font'
          label={'Select a Font'}
          isRequired={false}
          disabled={false}
          hideError
        />
        <FormikColorPicker
          label='Text Color'
          name='additionalTextColor'
          extendStyles={commonFormikColorPickerStyle}
        />
        <FormikColorPicker
          label='Background Color'
          name='additionalTextBackgroundColor'
          onChange={color => {
            setFieldValue('additionalTextBackgroundColor', color);
          }}
          extendStyles={commonFormikColorPickerStyle}
        />
      </FormSection>
    </FormContainer>
  );
};
